
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const JoinWaitlist = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !company || !role || !teamSize) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Insert into Supabase waitlist_entries table
      const { error } = await supabase
        .from('waitlist_entries')
        .insert([
          { 
            name,
            email,
            company,
            role,
            team_size: teamSize
          }
        ]);
      
      if (error) {
        console.error('Error submitting to waitlist:', error);
        throw error;
      }
      
      toast.success('You\'ve been added to our waitlist! We\'ll be in touch soon.');
      
      // Reset form
      setName('');
      setEmail('');
      setCompany('');
      setRole('');
      setTeamSize('');
    } catch (error) {
      toast.error('Failed to join waitlist. Please try again.');
      console.error('Waitlist submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const teamSizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501+', label: '501+ employees' }
  ];
  
  const roleOptions = [
    { value: 'executive', label: 'Executive (CEO, CTO, etc.)' },
    { value: 'director', label: 'Director/VP' },
    { value: 'manager', label: 'Manager/Team Lead' },
    { value: 'hr', label: 'HR/Talent Acquisition' },
    { value: 'individual', label: 'Individual Contributor' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="py-20 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]">
              Join Our Waitlist
            </h1>
            <p className="text-lg text-gray-700">
              Be among the first to experience the future of AI-augmented talent. Our platform is currently in beta, and we're accepting a limited number of companies and individuals.
            </p>
          </div>
          
          <Card className="max-w-2xl mx-auto border border-[#0EA5E9]/20 shadow-xl">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    id="name" 
                    placeholder="Your name" 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    required 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Work Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="your.email@company.com" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    required 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company">Company Name</Label>
                  <Input 
                    id="company" 
                    placeholder="Your company" 
                    value={company} 
                    onChange={(e) => setCompany(e.target.value)} 
                    required 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="role">Your Role</Label>
                  <Select value={role} onValueChange={setRole} required>
                    <SelectTrigger id="role">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="teamSize">Company Size</Label>
                  <Select value={teamSize} onValueChange={setTeamSize} required>
                    <SelectTrigger id="teamSize">
                      <SelectValue placeholder="Select company size" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamSizeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#0EA5E9] to-[#3B82F6] hover:from-[#0EA5E9]/90 hover:to-[#3B82F6]/90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
                </Button>
                
                <p className="text-xs text-center text-gray-500 mt-4">
                  By joining our waitlist, you agree to our Terms of Service and Privacy Policy.
                </p>
              </form>
            </CardContent>
          </Card>
          
          <div className="max-w-3xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-[#0EA5E9] font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Join Waitlist</h3>
              <p className="text-gray-600">Fill out the form to reserve your spot in line.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-[#0EA5E9] font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Early Access</h3>
              <p className="text-gray-600">Get notified when it's your turn to join the platform.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-[#0EA5E9] font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Personalized Setup</h3>
              <p className="text-gray-600">Receive a personalized onboarding experience.</p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default JoinWaitlist;
